import { useState, useMemo } from 'react';
import MUIDataTable from 'mui-datatables';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TableContainer,
} from '@mui/material';
import { useGetCaptureLeadsApiQuery, useGetTrialUsersQuery } from '../../../redux/services/settings/UserService';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import { showToast } from '../../../utils/toast';
import DataTableLazyLoading from '../../../components/lazyloading/DataTableLazyLoading';
import { sortedDataFn } from '../../../utils/getSortedData';

const TrialUsers = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDataIndex, setSelectedDataIndex] = useState(null);

  const { data: trialUsersResponse = {}, isLoading: isLoadingTrialUsers} = useGetTrialUsersQuery();
  const { data: captureLeadsResponse = [], isLoading: isLoadingCaptureLeads } = useGetCaptureLeadsApiQuery();

  const sortedData = useMemo(() => {
    const trialUsers = trialUsersResponse.list || [];
    return sortedDataFn(trialUsers);
  }, [trialUsersResponse]);

  const formattedCaptureLeads = useMemo(() => {
    const captureLeads = Array.isArray(captureLeadsResponse) ? captureLeadsResponse : [];
    if (!Array.isArray(captureLeads) || captureLeads.length === 0) return [];

    return captureLeads.map(lead => ({
      id: lead.id || '',
      fullname: lead.fullname || 'N/A',
      email: lead.email || 'N/A',
      phone: lead.phone || 'N/A',
      company: lead.company || 'N/A',
      designation: lead.designation || 'N/A',
      created: lead.created || lead.created_at || new Date().toISOString(),
    }));
  }, [captureLeadsResponse]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedDataIndex(null);
  };

  const handleSendEmail = async (userId) => {
    console.log("userId", userId);

    try {
      showToast('success', 'Email sent successfully!');
    } catch (error) {
      console.error('Error sending email:', error);
      showToast('error', error?.data?.message || 'Failed to send email');
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedDataIndex !== null) {
      // Handle delete logic here
      showToast('success', 'Trial user deleted successfully');
    }
    handleDialogClose();
  };

  const columns = [
    {
      name: 'first_name',
      label: 'Name',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { maxWidth: '250px', cursor: 'pointer' } }),
        customBodyRender: (value) => (
          <Typography variant="subtitle2" noWrap>
            {value}
          </Typography>
        ),
      },
    },
    {
      name: 'email',
      label: 'Email',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '200px' } }),
      },
    },
    {
      name: 'remaining_days',
      label: 'Remaining Days',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography variant="body2">
            {value} days
          </Typography>
        ),
      },
    },
  ];

  const captureLeadsColumns = [
    {
      name: 'fullname',
      label: 'Full Name',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { maxWidth: '200px' } }),
      },
    },
    {
      name: 'email',
      label: 'Email',
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '200px' } }),
      },
    },
    {
      name: 'phone',
      label: 'Phone',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'company',
      label: 'Company',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'designation',
      label: 'Designation',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'created',
      label: 'Created Date',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Typography variant="body2">
            {new Date(value).toLocaleString()}
          </Typography>
        ),
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const rowData = formattedCaptureLeads[tableMeta.rowIndex];
          console.log(rowData);

          if (!rowData) {
            console.warn('No row data found for index:', tableMeta.rowIndex);
            return null;
          }

          return (
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              // loading={isSendingEmail}
              onClick={() => handleSendEmail(rowData.id)}
              startIcon={<Iconify icon="mdi:email-send" />}
              disabled={!rowData.id}
            >
              Send Email
            </LoadingButton>
          );
        },
      },
    },
    // {
    //   name: 'phone_number',
    //   label: 'Phone',
    //   options: {
    //     filter: true,
    //     sort: true,
    //     setCellProps: () => ({ style: { minWidth: '150px' } }),
    //   },
    // },
    // {
    //   name: 'created_at',
    //   label: 'Signup Date',
    //   options: {
    //     filter: true,
    //     sort: true,
    //     customBodyRender: (value) => (
    //       <Typography variant="body2">
    //         {value ? new Date(value).toLocaleDateString() : '-'}
    //       </Typography>
    //     ),
    //   },
    // },
    // {
    //   name: 'actions',
    //   label: 'Actions',
    //   options: {
    //     filter: false,
    //     sort: false,
    //     empty: true,
    //     customBodyRender: (value, tableMeta) => (
    //       <Stack direction="row" spacing={1}>
    //         <CustomTooltip text="View">
    //           <IconButton
    //             size="small"
    //             color="info"
    //             onClick={() => navigate(`/dashboard/trial-users/${tableMeta.rowData[0]}`)}
    //           >
    //             <Iconify icon="eva:eye-outline" width={20} height={20} />
    //           </IconButton>
    //         </CustomTooltip>
    //         <CustomTooltip text="Delete">
    //           <IconButton
    //             size="small"
    //             color="error"
    //             onClick={() => handleDialogOpen(tableMeta.rowIndex)}
    //           >
    //             <Iconify icon="eva:trash-2-outline" width={20} height={20} />
    //           </IconButton>
    //         </CustomTooltip>
    //       </Stack>
    //     ),
    //   },
    // },
  ];

  const tableOptions = {
    filterType: 'dropdown',
    responsive: 'standard',
    serverSide: false,
    filter: true,
    search: true,
    download: false,
    print: false,
    viewColumns: true,
    selectableRows: 'none',
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    textLabels: {
      body: {
        noMatch: 'No data found',
        toolTip: 'Sort',
      },
      pagination: {
        next: 'Next Page',
        previous: 'Previous Page',
        rowsPerPage: 'Rows per page:',
        displayRows: 'of',
      },
    },
    setTableProps: () => ({
      style: {
        minWidth: '100%',
      },
    }),
    // customToolbar: () => {
    //   return (
    //     <Button
    //       variant="contained"
    //       startIcon={<Iconify icon="eva:plus-fill" />}
    //       onClick={() => navigate('/dashboard/trial-users/add')}
    //       sx={{ marginRight: 1 }}
    //     >
    //       Add Trial User
    //     </Button>
    //   );
    // },
  };

  if (isLoadingTrialUsers || isLoadingCaptureLeads) {
    return <DataTableLazyLoading />;
  }

  return (
    <Page title="Trial Users">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Trial Users
          </Typography>
        </Stack>

        <Card sx={{ mb: 4 }}>
          <TableContainer>
            <MUIDataTable
              title="Trial Users"
              data={sortedData}
              columns={columns}
              options={{
                ...tableOptions,
                textLabels: {
                  ...tableOptions.textLabels,
                  body: {
                    ...tableOptions.textLabels.body,
                    noMatch: 'No trial users found',
                  },
                },
              }}
            />
          </TableContainer>
        </Card>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Capture Leads
          </Typography>
        </Stack>
        <Card>
          <TableContainer>
            <MUIDataTable
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>Capture Leads</span>
                  {/* <Tooltip title="Refresh">
                    <IconButton 
                      size="small" 
                      onClick={refetchCaptureLeads}
                      disabled={isLoadingCaptureLeads}
                      style={{ marginLeft: 8 }}
                    >
                      <Iconify icon="eva:refresh-outline" width={20} height={20} />
                    </IconButton>
                  </Tooltip> */}
                </div>
              }
              data={formattedCaptureLeads}
              columns={captureLeadsColumns}
              options={{
                ...tableOptions,
                textLabels: {
                  ...tableOptions.textLabels,
                  body: {
                    ...tableOptions.textLabels.body,
                    noMatch: isLoadingCaptureLeads ? 'Loading...' : 'No capture leads found',
                  },
                },
                selectableRows: 'none',
                download: true,
                print: false,
                viewColumns: true,
                filter: true,
                filterType: 'dropdown',
                responsive: 'standard',
                rowsPerPage: 10,
                rowsPerPageOptions: [5, 10, 25, 50],
                onTableInit: () => { },
                onTableChange: () => { },
                serverSide: false
              }}
            />
          </TableContainer>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={dialogOpen} onClose={handleDialogClose}>
          <DialogTitle>Delete Trial User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this trial user? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default TrialUsers;
